import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, CheckCircle, Plug, Unplug, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import TimeMachineRevealModal, { useTimeMachineReveal } from '../components/TimeMachineRevealModal';

interface POSConnection {
  provider: string;
  merchant_name: string;
  merchant_id: string;
  last_synced_at: string | null;
  connected_at: string;
  location_ids: string[];
}

interface ProviderInfo {
  name: string;
  display_name: string;
  status: string; // "available" | "unconfigured" | "partner_access_pending"
  connect_modes?: string[]; // shopify: ["token"] or ["token", "oauth"]
}

const PROVIDER_LABELS: Record<string, string> = { square: 'Square', clover: 'Clover', shopify: 'Shopify' };
const labelFor = (provider: string) => PROVIDER_LABELS[provider] || 'your POS';

// Fallback catalog if GET /api/pos/providers is unavailable.
const DEFAULT_PROVIDERS: ProviderInfo[] = [
  { name: 'square', display_name: 'Square', status: 'available' },
  { name: 'clover', display_name: 'Clover', status: 'available' },
  { name: 'shopify', display_name: 'Shopify', status: 'available', connect_modes: ['token'] },
  { name: 'toast', display_name: 'Toast', status: 'partner_access_pending' },
  { name: 'lightspeed', display_name: 'Lightspeed', status: 'partner_access_pending' },
];

// Presentation details for the OAuth-connectable providers.
const PROVIDER_META: Record<string, {
  logo: JSX.Element;
  connectClasses: string;
  ordersNoun: string;
}> = {
  square: {
    logo: (
      <div className="w-12 h-12 bg-black rounded-[10px] flex items-center justify-center flex-shrink-0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <rect x="3" y="3" width="18" height="18" rx="2" fill="white" />
          <rect x="7" y="7" width="10" height="10" rx="1" fill="black" />
        </svg>
      </div>
    ),
    connectClasses: 'bg-black text-white hover:bg-gray-900',
    ordersNoun: 'completed orders',
  },
  clover: {
    logo: (
      <div className="w-12 h-12 bg-[#2E7D32] rounded-[10px] flex items-center justify-center flex-shrink-0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <circle cx="9" cy="9" r="4.2" />
          <circle cx="15" cy="9" r="4.2" />
          <circle cx="9" cy="15" r="4.2" />
          <circle cx="15" cy="15" r="4.2" />
        </svg>
      </div>
    ),
    connectClasses: 'bg-[#2E7D32] text-white hover:bg-[#256628]',
    ordersNoun: 'closed orders',
  },
  shopify: {
    logo: (
      <div className="w-12 h-12 bg-[#5E8E3E] rounded-[10px] flex items-center justify-center flex-shrink-0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M7 7.5 9.5 5h5L17 7.5 18.5 19H5.5L7 7.5Z" />
          <path d="M9.5 8.5v-1a2.5 2.5 0 0 1 5 0v1" fill="none" stroke="#5E8E3E" strokeWidth="1.5" />
        </svg>
      </div>
    ),
    connectClasses: 'bg-[#5E8E3E] text-white hover:bg-[#4F7A34]',
    ordersNoun: 'orders from every channel (online and POS)',
  },
};

// Registers that cannot be connected yet share one placeholder tile: the
// same 48px square as the live logos, plum-tinted, with a plug in it.
const PLACEHOLDER_LOGO = (
  <div className="w-12 h-12 bg-[#4A2D5E]/10 rounded-[10px] flex items-center justify-center flex-shrink-0">
    <Plug size={22} strokeWidth={1.5} className="text-[#4A2D5E]" aria-hidden="true" />
  </div>
);

const COMING_SOON = [{ name: 'PayPal Zettle' }];

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function POSPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState<ProviderInfo[]>(DEFAULT_PROVIDERS);
  const [connections, setConnections] = useState<POSConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingProvider, setSyncingProvider] = useState<string | null>(null);
  // "Had you been on ClearPath…" — fires once the post-connect sync brings real orders in.
  const tmReveal = useTimeMachineReveal();
  const [disconnectingProvider, setDisconnectingProvider] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);

  const isPro = user?.plan === 'pro' || user?.subscription_status === 'active';

  const fetchProviders = async () => {
    try {
      const res = await axios.get('/api/pos/providers');
      if (Array.isArray(res.data?.providers) && res.data.providers.length > 0) {
        setProviders(res.data.providers);
      }
    } catch {
      // keep the default catalog
    }
  };

  const fetchConnections = async () => {
    try {
      const res = await axios.get('/api/pos/connections');
      setConnections(res.data.connections);
    } catch {
      // silently ignore — user may not have any connections
    } finally {
      setLoading(false);
    }
  };

  // Shopify custom-app token door: the owner makes a custom app in their own
  // admin and pastes its Admin API token. The token lives in component state
  // only for the length of the request.
  const [shopifyShop, setShopifyShop] = useState('');
  const [shopifyToken, setShopifyToken] = useState('');
  const [shopifyGuideOpen, setShopifyGuideOpen] = useState(false);
  const [shopifyConnecting, setShopifyConnecting] = useState(false);

  // Handle redirect back from a provider's OAuth
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    fetchProviders();

    // Shopify's custom-distribution install link lands the merchant here with
    // ?shop=<store>.myshopify.com (plus hmac/timestamp). Prefill and open the
    // Shopify panel so the next click is "Connect with Shopify login".
    const shopParam = searchParams.get('shop');
    if (shopParam && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopParam)) {
      setShopifyShop(shopParam.toLowerCase());
      setShopifyGuideOpen(true);
      setSearchParams({}, { replace: true });
    }

    if (connected === 'square' || connected === 'clover' || connected === 'shopify') {
      const label = labelFor(connected);
      setSearchParams({}, { replace: true });
      fetchConnections().then(() => {
        toast.success(`${label} connected! Syncing your data now…`);
        handleSync(connected, true);
      });
    } else if (error) {
      setSearchParams({}, { replace: true });
      const label = labelFor(error.split('_')[0]);
      toast.error(`Could not connect to ${label}. Please try again.`);
      setLoading(false);
    } else {
      fetchConnections();
    }
  }, []);

  const handleConnect = (provider: string) => {
    if (!isPro) {
      toast.error('POS integrations require a Pro plan. Upgrade in Settings.');
      return;
    }
    setConnectingProvider(provider);
    const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const qs = provider === 'shopify' ? `?shop=${encodeURIComponent(shopifyShop.trim())}` : '';
    window.location.href = `${API}/api/pos/${provider}/connect${qs}`;
  };

  const handleShopifyTokenConnect = async () => {
    if (!isPro) {
      toast.error('POS integrations require a Pro plan. Upgrade in Settings.');
      return;
    }
    if (!shopifyShop.trim() || !shopifyToken.trim()) {
      toast.error('Enter your store address and the Admin API access token');
      return;
    }
    setShopifyConnecting(true);
    try {
      const res = await axios.post('/api/pos/shopify/connect-token', {
        shop: shopifyShop.trim(),
        token: shopifyToken.trim(),
      });
      setShopifyToken('');
      const added = res.data?.sync?.transactions_added ?? 0;
      toast.success(`${res.data.merchant_name} connected. Synced ${added} transactions.`);
      await fetchConnections();
      if (added > 0) tmReveal.reveal();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not connect Shopify. Check the address and token.');
    } finally {
      setShopifyConnecting(false);
    }
  };

  const handleSync = async (provider: string, silent = false) => {
    const label = labelFor(provider);
    setSyncingProvider(provider);
    try {
      const res = await axios.post(`/api/pos/${provider}/sync`);
      const added = res.data.transactions_added;
      if (!silent) {
        toast.success(added > 0 ? `Synced ${added} new transactions from ${label}` : 'Already up to date');
      }
      await fetchConnections();
      if (silent && added > 0) tmReveal.reveal(); // silent === the post-connect first sync
    } catch (err: any) {
      if (!silent) {
        toast.error(err.response?.data?.detail || 'Sync failed. Please try again.');
      }
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleDisconnect = async (provider: string) => {
    const label = labelFor(provider);
    if (!window.confirm(`Disconnect ${label}? Your already-synced data will stay in ClearPath.`)) return;
    setDisconnectingProvider(provider);
    try {
      await axios.delete(`/api/pos/${provider}/disconnect`);
      setConnections(prev => prev.filter(c => c.provider !== provider));
      toast.success(`${label} disconnected`);
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const oauthProviders = providers.filter(p => PROVIDER_META[p.name]);
  const pendingProviders = providers.filter(p => p.status === 'partner_access_pending');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="p-6 md:p-10 max-w-3xl mx-auto"
    >
      <TimeMachineRevealModal state={tmReveal.state} report={tmReveal.report} onClose={tmReveal.close} />
      <div className="mb-8">
        <h1 className="font-serif text-2xl text-[#2D2A2E]">Integrations</h1>
        <p className="mt-1 font-sans text-sm text-[#6B6670]">
          Connect your POS directly — your data syncs automatically, no CSV exports needed.
        </p>
      </div>

      {/* Pro gate banner */}
      {!isPro && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-[#4A2D5E]/5 border border-[#4A2D5E]/15 rounded-[12px]">
          <Lock size={18} className="text-[#4A2D5E] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-sans text-sm font-medium text-[#2D2A2E]">Pro feature</p>
            <p className="font-sans text-xs text-[#6B6670] mt-0.5">
              POS integrations are available on the Pro plan.{' '}
              <button onClick={() => navigate('/settings')} className="text-[#4A2D5E] font-medium hover:underline">
                Upgrade in Settings →
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Provider cards (Square, Clover) */}
      {oauthProviders.map(providerInfo => {
        const meta = PROVIDER_META[providerInfo.name];
        const label = providerInfo.display_name;
        const conn = connections.find(c => c.provider === providerInfo.name) || null;
        const syncing = syncingProvider === providerInfo.name;
        const disconnecting = disconnectingProvider === providerInfo.name;
        const connecting = connectingProvider === providerInfo.name;
        const unconfigured = providerInfo.status === 'unconfigured';

        return (
          <div key={providerInfo.name} className="bg-white border border-[#E6E2DE] rounded-[14px] shadow-sm overflow-hidden mb-4">
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  {meta.logo}
                  <div>
                    <h3 className="font-serif text-lg text-[#2D2A2E]">{label}</h3>
                    <p className="font-sans text-xs text-[#6B6670] mt-0.5">
                      {conn
                        ? conn.merchant_name || `Merchant ${conn.merchant_id}`
                        : 'Connect to sync orders automatically'}
                    </p>
                  </div>
                </div>

                {/* Status badge */}
                {conn ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-sans bg-[#2E6B4F]/10 text-[#2E6B4F] flex-shrink-0">
                    <CheckCircle size={12} strokeWidth={2} />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium font-sans bg-[#E6E2DE] text-[#6B6670] flex-shrink-0">
                    Not connected
                  </span>
                )}
              </div>

              {/* Connected state details */}
              {conn && (
                <div className="mt-5 pt-5 border-t border-[#E6E2DE]">
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div>
                      <p className="font-sans text-xs text-[#6B6670] mb-0.5">Last synced</p>
                      <p className="font-sans text-sm text-[#2D2A2E] flex items-center gap-1.5">
                        <Clock size={13} strokeWidth={1.5} className="text-[#7C7589]" />
                        {timeAgo(conn.last_synced_at)}
                      </p>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-[#6B6670] mb-0.5">Locations</p>
                      <p className="font-sans text-sm text-[#2D2A2E]">
                        {conn.location_ids?.length || 0} active
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSync(providerInfo.name, false)}
                      disabled={syncing}
                      className="flex items-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] font-sans font-medium text-sm px-4 py-2 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={14} strokeWidth={1.5} className={syncing ? 'animate-spin' : ''} />
                      {syncing ? 'Syncing…' : 'Sync Now'}
                    </button>
                    <button
                      onClick={() => handleDisconnect(providerInfo.name)}
                      disabled={disconnecting}
                      className="flex items-center gap-2 bg-white text-[#B54A4A] border border-[#B54A4A]/30 hover:bg-[#B54A4A]/5 rounded-[8px] font-sans font-medium text-sm px-4 py-2 transition-colors disabled:opacity-50"
                    >
                      <Unplug size={14} strokeWidth={1.5} />
                      {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </div>
                </div>
              )}

              {/* Not connected state — Shopify: custom-app token door, plus OAuth when the Partner app is live */}
              {!conn && !loading && providerInfo.name === 'shopify' && (
                <div className="mt-5 pt-5 border-t border-[#E6E2DE] space-y-4">
                  {!shopifyGuideOpen ? (
                    <button
                      onClick={() => setShopifyGuideOpen(true)}
                      disabled={!isPro}
                      className={`flex items-center gap-2 ${meta.connectClasses} rounded-[8px] font-sans font-medium text-sm px-5 py-2.5 transition-colors disabled:opacity-50`}
                    >
                      <Plug size={14} strokeWidth={1.5} />
                      Connect Shopify
                    </button>
                  ) : (
                    <>
                      {providerInfo.connect_modes?.includes('oauth') ? (
                        <p className="font-sans text-sm text-[#2D2A2E]">
                          Enter your store address and click <strong>Connect with Shopify login</strong>. Shopify will ask you to approve read-only access to orders and products, then send you back here.
                          If ClearPath sent you an install link, open it first so Shopify recognises your store.
                        </p>
                      ) : (
                        <p className="font-sans text-sm text-[#2D2A2E]">
                          Enter your store address and the Admin API access token from a ClearPath custom app (it starts with <code>shpat_</code>). Since January 2026 Shopify no longer lets stores create these themselves, so if you don't have one, ask ClearPath for an install link instead.
                        </p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          value={shopifyShop}
                          onChange={(e) => setShopifyShop(e.target.value)}
                          placeholder="your-store.myshopify.com"
                          autoComplete="off"
                          className="w-full border border-[#E6E2DE] rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]"
                        />
                        <input
                          type="password"
                          value={shopifyToken}
                          onChange={(e) => setShopifyToken(e.target.value)}
                          placeholder="shpat_…"
                          autoComplete="off"
                          className="w-full border border-[#E6E2DE] rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]"
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        {providerInfo.connect_modes?.includes('oauth') && (
                          <button
                            onClick={() => handleConnect('shopify')}
                            disabled={connecting || !isPro || !shopifyShop.trim()}
                            className={`flex items-center gap-2 ${meta.connectClasses} rounded-[8px] font-sans font-medium text-sm px-5 py-2.5 transition-colors disabled:opacity-50`}
                          >
                            <Plug size={14} strokeWidth={1.5} />
                            {connecting ? 'Redirecting to Shopify…' : 'Connect with Shopify login'}
                          </button>
                        )}
                        <button
                          onClick={handleShopifyTokenConnect}
                          disabled={shopifyConnecting || !isPro || !shopifyToken.trim()}
                          className={`flex items-center gap-2 ${providerInfo.connect_modes?.includes('oauth') ? 'bg-white text-[#2D2A2E] border border-[#E6E2DE] hover:bg-[#FAF9F7]' : meta.connectClasses} rounded-[8px] font-sans font-medium text-sm px-4 py-2.5 transition-colors disabled:opacity-50`}
                        >
                          {shopifyConnecting ? 'Connecting…' : providerInfo.connect_modes?.includes('oauth') ? 'Connect with a token instead' : 'Connect'}
                        </button>
                        <button onClick={() => setShopifyGuideOpen(false)} className="font-sans text-xs text-[#6B6670] hover:text-[#4A2D5E]">
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  {!isPro && (
                    <p className="font-sans text-xs text-[#6B6670]">Pro plan required</p>
                  )}
                </div>
              )}

              {/* Not connected state */}
              {!conn && !loading && providerInfo.name !== 'shopify' && (
                <div className="mt-5 pt-5 border-t border-[#E6E2DE]">
                  <button
                    onClick={() => handleConnect(providerInfo.name)}
                    disabled={connecting || !isPro || unconfigured}
                    className={`flex items-center gap-2 ${meta.connectClasses} rounded-[8px] font-sans font-medium text-sm px-5 py-2.5 transition-colors disabled:opacity-50`}
                  >
                    <Plug size={14} strokeWidth={1.5} />
                    {connecting ? `Redirecting to ${label}…` : `Connect ${label}`}
                  </button>
                  {unconfigured && (
                    <p className="mt-2 font-sans text-xs text-[#6B6670]">Temporarily unavailable — check back soon</p>
                  )}
                  {!unconfigured && !isPro && (
                    <p className="mt-2 font-sans text-xs text-[#6B6670]">Pro plan required</p>
                  )}
                </div>
              )}
            </div>

            {/* How it works footer */}
            <div className="px-6 py-4 bg-[#FAF9F7] border-t border-[#E6E2DE]">
              <p className="font-sans text-xs text-[#6B6670] leading-relaxed">
                <strong className="text-[#2D2A2E]">How it works:</strong>{' '}
                {providerInfo.name === 'shopify'
                  ? <>ClearPath reads your {meta.ordersNoun} with a read-only token you create in your own Shopify admin. It never asks for customer names, emails or addresses, and you can revoke it any time by uninstalling the custom app.</>
                  : <>ClearPath uses {label}'s OAuth to read your {meta.ordersNoun}. We never store your {label} credentials and only request read-only access.</>}
                {' '}Sync runs on demand and nightly.
              </p>
            </div>
          </div>
        );
      })}

      {/* Partner access pending */}
      {pendingProviders.length > 0 && (
        <>
          <h2 className="font-serif text-base text-[#2D2A2E] mt-8 mb-3">In the works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pendingProviders.map(p => (
              <div
                key={p.name}
                className="bg-white border border-[#E6E2DE] rounded-[12px] p-4 flex items-center gap-3 opacity-60"
              >
                {PLACEHOLDER_LOGO}
                <div>
                  <p className="font-sans text-sm font-medium text-[#2D2A2E]">{p.display_name}</p>
                  <p className="font-sans text-xs text-[#6B6670]">Partner access pending</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Coming soon */}
      <h2 className="font-serif text-base text-[#2D2A2E] mt-8 mb-3">Coming soon</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {COMING_SOON.map(pos => (
          <div
            key={pos.name}
            className="bg-white border border-[#E6E2DE] rounded-[12px] p-4 flex items-center gap-3 opacity-60"
          >
            {PLACEHOLDER_LOGO}
            <div>
              <p className="font-sans text-sm font-medium text-[#2D2A2E]">{pos.name}</p>
              <p className="font-sans text-xs text-[#6B6670]">Coming soon</p>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-6 font-sans text-xs text-[#7C7589]">
        Want a specific POS added sooner?{' '}
        <a href="mailto:support@clearpathsmb.com" className="text-[#4A2D5E] hover:underline">
          Let us know →
        </a>
      </p>
    </motion.div>
  );
}
